import { Box } from '@mui/material'
import { HandoverIcon, ShapesIcon, TruckIcon, useMinimalsTheme } from '@tom-ui/ui'
import { LayoutObjectType } from '@yard/app/api'
import { layoutObjectService } from '@yard/services/layoutObject.service'
import { observer } from 'mobx-react-lite'
import { ReactNode, useEffect, useState } from 'react'
import { NodeProps } from 'reactflow'
import { LayoutObjectWithActions } from '../../models/yard-view.model'
import { FocusWrapper } from '../molecules/FocusWrapper'
import { LayoutObjectHeader } from '../molecules/LayoutObjectHeader'
import { NodeResizer } from '../molecules/NodeResizer'
import { RotatableNodeWrapper } from '../molecules/RotatableNodeWrapper'

const layoutObjectIconMap: Record<string, ReactNode> = {
  [LayoutObjectType.HoldingPoint]: <TruckIcon />,
  [LayoutObjectType.HandoverArea]: <HandoverIcon />,
}

const getLayoutObjectIcon = (type: LayoutObjectType): ReactNode =>
  layoutObjectIconMap[type] ?? <ShapesIcon />

export const LayoutObjectNode = observer(({ data }: NodeProps<LayoutObjectWithActions>) => {
  const {
    id,
    name,
    description,
    color,
    opacity,
    type,
    focused,
    showNodeHeaders,
    isEditable,
    layoutPosition,
    onRotate,
    onFocus,
    onEdit,
    onDelete,
    onResize,
  } = data

  const theme = useMinimalsTheme()
  const [image, setImage] = useState<string>()

  useEffect(() => {
    const fetchImage = async () => {
      if (data.imagePath) {
        const file = await layoutObjectService.getLayoutObjectImage(data.id)
        const url = URL.createObjectURL(file)
        setImage(url)
      } else {
        setImage(undefined)
      }
    }

    fetchImage()

    return () => {
      if (image) {
        URL.revokeObjectURL(image)
      }
    }
  }, [data.id, data.imagePath])

  const icon = getLayoutObjectIcon(type)

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <RotatableNodeWrapper
        rotation={layoutPosition.rotation}
        enabled={focused && isEditable}
        onRotationEnd={angle => onRotate(id, angle)}
        parentDeterminesSize
        header={
          focused || showNodeHeaders ? (
            <LayoutObjectHeader
              id={id}
              name={name}
              description={description}
              focused={focused}
              isEditable={isEditable}
              icon={icon}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ) : undefined
        }
      >
        <FocusWrapper
          focused={focused}
          isEditable={isEditable}
          onFocus={() => onFocus(id)}
          height={'100%'}
          width={'100%'}
          parentDeterminesSize
          backgroundColor={color ?? undefined}
          backgroundImage={image?.toString()}
          backgroundImageOpacity={opacity ?? 1}
        >
          <Box
            sx={{
              color: theme.palette.text.secondary,
              '& .MuiSvgIcon-root': { fontSize: '2rem' },
            }}
          >
            {icon}
          </Box>
          <NodeResizer
            isEditable={isEditable && focused}
            onResize={(width, height) => onResize(id, width, height)}
          />
        </FocusWrapper>
      </RotatableNodeWrapper>
    </Box>
  )
})
